import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { isWeb } from '../utils/platformUtils';
import AuthSignInForm from '../components/auth/AuthSignInForm';
import AuthSplitLayout from '../components/auth/AuthSplitLayout';
import AuthBrandHeader from '../components/auth/AuthBrandHeader';
import SignupScreen from './SignupScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import { useAuth } from '../context/AuthContext';
import {
  formatSocialAuthError,
  getAuthErrorMessage,
  isInvalidCredentialError,
} from '../utils/authErrors';

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const WelcomeScreen = () => {
  const { colors } = useTheme();
  const { login, signInWithGoogle, signInWithApple } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [socialError, setSocialError] = useState('');
  const [authView, setAuthView] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [validationErrors, setValidationErrors] = useState({ email: '', password: '' });

  const validateForm = () => {
    const errors = { email: '', password: '' };
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!password.trim()) {
      errors.password = 'Password is required';
    }
    setValidationErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setValidationErrors({ email: '', password: '' });

    try {
      await login(email, password);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      if (isInvalidCredentialError(error)) {
        setValidationErrors({ email: '', password: errorMessage });
      } else if (errorMessage.includes('Too many failed attempts')) {
        setValidationErrors({ email: '', password: errorMessage });
      } else if (errorMessage.includes('Network error')) {
        Alert.alert('Error', errorMessage);
      } else {
        setValidationErrors({ email: '', password: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setSocialError('');
    setValidationErrors({ email: '', password: '' });
    setSocialLoading('google');
    try {
      await signInWithGoogle();
    } catch (error) {
      const message = formatSocialAuthError(error, 'Google sign-in failed.');
      if (message) setSocialError(message);
    } finally {
      setSocialLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setSocialError('');
    setValidationErrors({ email: '', password: '' });
    setSocialLoading('apple');
    try {
      await signInWithApple();
    } catch (error) {
      const message = formatSocialAuthError(error, 'Apple sign-in failed.');
      if (message) setSocialError(message);
    } finally {
      setSocialLoading(null);
    }
  };

  if (authView === 'signup') {
    return <SignupScreen onBack={() => setAuthView('signin')} />;
  }

  if (authView === 'forgot') {
    return (
      <ForgotPasswordScreen onBack={() => setAuthView('signin')} />
    );
  }

  const signInForm = (onDarkPanel?: boolean) => (
    <AuthSignInForm
      onDarkPanel={onDarkPanel}
      email={email}
      onEmailChange={text => {
        setEmail(text);
        if (validationErrors.email) {
          setValidationErrors(prev => ({ ...prev, email: '' }));
        }
      }}
      password={password}
      onPasswordChange={text => {
        setPassword(text);
        if (validationErrors.password) {
          setValidationErrors(prev => ({ ...prev, password: '' }));
        }
      }}
      showPassword={showPassword}
      onTogglePassword={() => setShowPassword(!showPassword)}
      validationErrors={validationErrors}
      isLoading={isLoading}
      socialLoading={socialLoading}
      socialError={socialError}
      onSignIn={handleLogin}
      onGoogleSignIn={handleGoogleSignIn}
      onAppleSignIn={handleAppleSignIn}
      onForgotPassword={() => setAuthView('forgot')}
      onCreateAccount={() => setAuthView('signup')}
    />
  );

  if (isWeb) {
    return (
      <AuthSplitLayout title="Welcome back" subtitle="Sign in to your account">
        {signInForm()}
      </AuthSplitLayout>
    );
  }

  const panelColor = colors.auth.panel;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: panelColor }]}>
      <KeyboardAvoidingView
        style={[styles.flex, { backgroundColor: panelColor }]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          style={{ backgroundColor: panelColor }}
          contentContainerStyle={[styles.mobileScroll, { backgroundColor: panelColor }]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.mobileBrandSection, { backgroundColor: panelColor }]}>
            <AuthBrandHeader showTagline />
          </View>
          <View style={styles.mobileFormSection}>
            <Text style={styles.mobileTitle}>Welcome back</Text>
            <Text style={styles.mobileSubtitle}>Sign in to your account</Text>
            {signInForm(true)}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  flex: {
    flex: 1,
  },
  mobileScroll: {
    flexGrow: 1,
    paddingBottom: 32,
  },
  mobileBrandSection: {
    width: '100%',
    alignItems: 'center',
    paddingTop: 24,
    paddingBottom: 28,
    paddingHorizontal: 24,
  },
  mobileFormSection: {
    paddingHorizontal: 24,
    paddingBottom: 8,
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
  },
  mobileTitle: {
    fontSize: 26,
    fontWeight: '700',
    marginBottom: 6,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  mobileSubtitle: {
    fontSize: 15,
    marginBottom: 24,
    lineHeight: 22,
    color: 'rgba(255,255,255,0.75)',
    textAlign: 'center',
  },
});

export default WelcomeScreen;
